/**
 * Password Policy for CFR 21 Part 11 Compliance
 *
 * Requirements:
 * - Minimum 12 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 number
 * - At least 1 special character
 * - Cannot contain email or name
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'fair' | 'good' | 'strong';
}

export interface PasswordRequirement {
  label: string;
  test: (password: string, context?: PasswordContext) => boolean;
  met: boolean;
}

export interface PasswordContext {
  email?: string;
  fullName?: string;
}

const MIN_LENGTH = 12;
const SPECIAL_CHARS = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

/**
 * Validates a password against CFR 21 Part 11 requirements
 */
export function validatePassword(
  password: string,
  context?: PasswordContext
): PasswordValidationResult {
  const errors: string[] = [];

  if (password.length < MIN_LENGTH) {
    errors.push(`Password must be at least ${MIN_LENGTH} characters`);
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!SPECIAL_CHARS.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  if (context?.email) {
    const emailLocal = context.email.split('@')[0].toLowerCase();
    if (emailLocal.length >= 3 && password.toLowerCase().includes(emailLocal)) {
      errors.push('Password cannot contain your email address');
    }
  }

  if (context?.fullName) {
    const nameParts = context.fullName.toLowerCase().split(/\s+/);
    for (const part of nameParts) {
      if (part.length >= 3 && password.toLowerCase().includes(part)) {
        errors.push('Password cannot contain your name');
        break;
      }
    }
  }

  const strength = calculatePasswordStrength(password);

  return {
    isValid: errors.length === 0,
    errors,
    strength,
  };
}

/**
 * Get individual password requirements with their status
 */
export function getPasswordRequirements(
  password: string,
  context?: PasswordContext
): PasswordRequirement[] {
  return [
    {
      label: `At least ${MIN_LENGTH} characters`,
      test: (p) => p.length >= MIN_LENGTH,
      met: password.length >= MIN_LENGTH,
    },
    {
      label: 'One uppercase letter',
      test: (p) => /[A-Z]/.test(p),
      met: /[A-Z]/.test(password),
    },
    {
      label: 'One lowercase letter',
      test: (p) => /[a-z]/.test(p),
      met: /[a-z]/.test(password),
    },
    {
      label: 'One number',
      test: (p) => /[0-9]/.test(p),
      met: /[0-9]/.test(password),
    },
    {
      label: 'One special character (!@#$%^&*...)',
      test: (p) => SPECIAL_CHARS.test(p),
      met: SPECIAL_CHARS.test(password),
    },
    {
      label: 'Does not contain email or name',
      test: (p, ctx) => {
        if (!ctx) return true;
        const emailLocal = ctx.email?.split('@')[0].toLowerCase() || '';
        if (emailLocal.length >= 3 && p.toLowerCase().includes(emailLocal)) return false;
        const nameParts = ctx.fullName?.toLowerCase().split(/\s+/) || [];
        for (const part of nameParts) {
          if (part.length >= 3 && p.toLowerCase().includes(part)) return false;
        }
        return true;
      },
      met: (() => {
        if (!context) return true;
        const emailLocal = context.email?.split('@')[0].toLowerCase() || '';
        if (emailLocal.length >= 3 && password.toLowerCase().includes(emailLocal)) return false;
        const nameParts = context.fullName?.toLowerCase().split(/\s+/) || [];
        for (const part of nameParts) {
          if (part.length >= 3 && password.toLowerCase().includes(part)) return false;
        }
        return true;
      })(),
    },
  ];
}

/**
 * Calculate password strength for UI feedback
 */
function calculatePasswordStrength(password: string): 'weak' | 'fair' | 'good' | 'strong' {
  let score = 0;

  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 20) score += 1;

  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (SPECIAL_CHARS.test(password)) score += 1;

  if (/[a-z].*[A-Z]|[A-Z].*[a-z]/.test(password)) score += 1;
  if (/[0-9].*[a-zA-Z]|[a-zA-Z].*[0-9]/.test(password)) score += 1;

  if (score < 4) return 'weak';
  if (score < 6) return 'fair';
  if (score < 8) return 'good';
  return 'strong';
}

/**
 * Get color class for password strength indicator
 */
export function getStrengthColor(strength: 'weak' | 'fair' | 'good' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'bg-red-500';
    case 'fair':
      return 'bg-yellow-500';
    case 'good':
      return 'bg-blue-500';
    case 'strong':
      return 'bg-green-500';
  }
}

/**
 * Get width percentage for password strength bar
 */
export function getStrengthWidth(strength: 'weak' | 'fair' | 'good' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return '25%';
    case 'fair':
      return '50%';
    case 'good':
      return '75%';
    case 'strong':
      return '100%';
  }
}
